// Generic clamp-and-expand hook (D60, D61).
//
// Measures whether a clamped element actually overflows its container. When
// it does, reports `overflows: true` so the consumer can render the fade
// overlay. Clicking toggles between clamped and expanded; clicking outside
// or pressing Esc collapses back.

import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseExpandableOptions {
  /** Maximum visible lines when collapsed. */
  maxLines?: number;
}

export interface UseExpandableReturn {
  /** Whether the element is currently expanded. */
  expanded: boolean;
  /** Toggle expanded state. */
  toggle: () => void;
  /** Collapse (for external triggers like Esc or click-outside). */
  collapse: () => void;
  /** Whether the content actually overflows the clamped height. */
  overflows: boolean;
  /** Callback ref — attach to the element whose height is measured. */
  ref: (node: HTMLElement | null) => void;
}

export function useExpandable(
  opts: UseExpandableOptions = {},
): UseExpandableReturn {
  const { maxLines = 3 } = opts;
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const nodeRef = useRef<HTMLElement | null>(null);

  const measure = useCallback(
    (el: HTMLElement | null) => {
      if (!el) {
        setOverflows(false);
        return;
      }
      const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 24;
      const maxHeight = lineHeight * maxLines;
      setOverflows(el.scrollHeight > maxHeight + 2);
    },
    [maxLines],
  );

  const ref = useCallback(
    (node: HTMLElement | null) => {
      nodeRef.current = node;
      measure(node);
    },
    [measure],
  );

  // Re-measure when the content changes (e.g. after a re-render).
  useEffect(() => {
    measure(nodeRef.current);
  });

  // Collapse on Esc.
  useEffect(() => {
    if (!expanded) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setExpanded(false);
      }
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [expanded]);

  // Collapse on click-outside.
  useEffect(() => {
    if (!expanded) return;
    function handleClick(e: MouseEvent) {
      if (
        nodeRef.current &&
        e.target instanceof Node &&
        !nodeRef.current.contains(e.target)
      ) {
        setExpanded(false);
      }
    }
    // Use a microtask delay so the toggle click that opened it doesn't
    // immediately re-collapse via the same event propagating to document.
    const id = requestAnimationFrame(() => {
      document.addEventListener('mousedown', handleClick);
    });
    return () => {
      cancelAnimationFrame(id);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [expanded]);

  const toggle = useCallback(() => setExpanded((v) => !v), []);
  const collapse = useCallback(() => setExpanded(false), []);

  return { expanded, toggle, collapse, overflows, ref };
}
